import { styled } from "goober";
import { useMemo } from "react";
import { GIT_SYNC_FEATURE_FLAG } from "../../shared/featureFlagNames";
import { queries } from "../../shared/queries";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { useGroupMemberships } from "../hooks/useGroupMemberships";
import { useQuery } from "../hooks/useQuery";
import { checkMoveTargetAccess } from "../lib/checkMoveTargetAccess";
import { Button } from "./Button";
import { Icon, type IconName } from "./Icon";
import { Modal } from "./Modal";
import { Text } from "./Text";

type Consequence = { title: string; detail: string; icon: IconName };

// Only shown when the workflow is live: a move never carries liveness into
// the target space.
const LIVE_CONSEQUENCE: Consequence = {
  title: "The live version becomes a draft",
  icon: "git-branch",
  detail:
    "Nothing stays live after the move — public URLs stop serving and schedules stop. The live version becomes a draft owned by you; push it live in the new space to bring the workflow back online, where URLs use the new space’s subdomain.",
};

// Everything that can break or change when a workflow moves spaces. Shown
// unconditionally so the user always sees the full picture before confirming.
const CONSEQUENCES: Consequence[] = [
  {
    title: "Endpoint access resets",
    icon: "key",
    detail:
      "API keys and link sign-ins scoped to the current space no longer authenticate this workflow’s endpoints.",
  },
  {
    title: "Space resources don’t move",
    icon: "hash",
    detail:
      "Runs use the new space’s connectors, networks, and skills. If it can’t access something this workflow relies on, runs may fail.",
  },
  {
    title: "Volumes stay behind",
    icon: "box",
    detail:
      "Named volumes stay registered to the current space and won’t appear in the new space until the workflow runs there again.",
  },
  {
    title: "Past runs stay behind",
    icon: "play",
    detail:
      "Runs belong to the space they ran in, so this workflow’s run history and step-usage counts stay with the current space — only new runs count in the new space.",
  },
];

// Only relevant when git sync is enabled, so gated behind the same flag as the
// git sync settings form.
const GIT_SYNC_CONSEQUENCE: Consequence = {
  title: "Git sync changes",
  icon: "git-branch",
  detail:
    "If either space syncs to Git, this workflow stops syncing to the current space’s repository and follows the new space’s setup instead.",
};

export function MoveWorkflowModal({
  workflowId,
  targetSpaceId,
  targetSpaceName,
  onClose,
  onConfirm,
}: {
  workflowId: string;
  targetSpaceId: string;
  targetSpaceName: string;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const gitSyncEnabled = useFeatureFlag(GIT_SYNC_FEATURE_FLAG);
  const [workflow, workflowDetails] = useQuery(
    queries.workflowMainRoutes({ workflowId })
  );
  const [workflowApps] = useQuery(queries.workflowAppSlugs({ workflowId }));
  const [allApps] = useQuery(queries.connectedAppsIndex());
  const [spaces] = useQuery(queries.spaces({ settingsView: true }));
  const { userGroupIds: userGroupMemberships } = useGroupMemberships();

  const disconnecting = useMemo(() => {
    const targetSpace = spaces.find((s) => s.id === targetSpaceId);
    if (!targetSpace) return [];
    const { missingAppsBySpace } = checkMoveTargetAccess({
      spaces: [targetSpace],
      workflowAppIds: new Set(workflowApps.map((a) => a.connectedAppId)),
      allApps,
      userGroupMemberships,
    });
    return missingAppsBySpace.get(targetSpaceId) ?? [];
  }, [spaces, targetSpaceId, workflowApps, allApps, userGroupMemberships]);

  if (workflowDetails.type !== "complete") return null;

  const isLive = !!workflow?.branches;
  const consequences = [
    ...(disconnecting.length > 0
      ? [
          {
            title: "Connectors removed",
            icon: "api-key-custom" as IconName,
            detail: `${targetSpaceName} can’t use ${disconnecting.join(", ")}, so moving the workflow removes ${disconnecting.length === 1 ? "it" : "them"} — affected steps will need a replacement.`,
          },
        ]
      : []),
    ...(isLive ? [LIVE_CONSEQUENCE] : []),
    ...CONSEQUENCES,
    ...(gitSyncEnabled ? [GIT_SYNC_CONSEQUENCE] : []),
  ];

  return (
    <Modal open onClose={onClose} alert>
      <Content>
        <TitleBlock>
          <Text variant="text-regular-md" color="var(--text-primary)">
            Move to {targetSpaceName}?
          </Text>
          <Text variant="text-regular" color="var(--text-secondary)">
            The workflow and its branches and commits move with it. These things
            stay behind or change:
          </Text>
        </TitleBlock>
        <List>
          {consequences.map((c) => (
            <Row key={c.title}>
              <LogoSlot>
                <Icon name={c.icon} size={16} color="var(--text-primary)" />
              </LogoSlot>
              <RowText>
                <Text variant="text-regular" color="var(--text-primary)">
                  {c.title}
                </Text>
                <Text variant="text-small" color="var(--text-secondary)">
                  {c.detail}
                </Text>
              </RowText>
            </Row>
          ))}
        </List>
        <Footer>
          <Button size="l" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button size="l" onClick={onConfirm}>
            Move workflow
          </Button>
        </Footer>
      </Content>
    </Modal>
  );
}

const Content = styled("div")`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 400px;
`;

const TitleBlock = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const List = styled("div")`
  display: flex;
  flex-direction: column;
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  background: var(--surface-secondary);
  overflow: hidden;
`;

const Row = styled("div")`
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  &:not(:last-child) {
    border-bottom: 0.5px solid var(--border-alpha);
  }
`;

const RowText = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 2px;
`;

const LogoSlot = styled("div")`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding-top: 2px;
`;

const Footer = styled("div")`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;
